import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  ApiErrorCodes,
  parseApiResponseError,
} from '../../../../api/api-error';
import apiManager from '../../../../api/api-manager';
import {
  AggiornaCellulareError,
  AggiornaCellulareErrorCodiciErroreEnum,
  AggiornaEmailError,
  AggiornaEmailErrorCodiciErroreEnum,
  Persona,
  UtentiError,
  UtentiErrorCodiciErroreEnum,
  VerificaCellulareError,
  VerificaCellulareErrorCodiciErroreEnum,
  VerificaEmailError,
  VerificaEmailErrorCodiciErroreEnum,
  VerificaCellulareResponse,
  CodiceClienteRappresentante,
  VerificaEmailResponse,
  StatusRegistrazione,
} from '../../../../api/generated/apc/api';
import { parsePhoneNumberForServer } from '../../../../api/parsers/commons-api-parsers';
import { SignatureDocument } from '../../../../model';
import signatureAreaRepository from '../../../../repositories/signature-area/signature-area-repository';
import { AsyncThunkConfig } from '../../../async-thunk-config';
import {
  InsertEmailError,
  InsertFiscalCodeLegalPersonError,
  InsertPhoneNumberError,
  PrepareRegistrationError,
  PrivacyAgreementError,
  SetupAccountError,
  SignatureDocumentError,
  VerificationEmailError,
  VerificationPhoneNumberError,
} from './user-registration-types';
import {
  getSetupAccountError,
  parseCreateUserRequest,
  verifyInsertEmailFields,
  verifyInsertFiscalCodeLegalPersonFields,
  verifyInsertPhoneNumberFields,
  verifyInsertSecondaryPhoneNumberFields,
  verifySetupAccountFields,
} from './user-registration-utils';

export const getStatusRegistrazione = createAsyncThunk<
  StatusRegistrazione,
  void,
  AsyncThunkConfig<SetupAccountError | StatusRegistrazione>
>('get_status_registrazione', async (_, thunkApi) => {
  try {
    const state = thunkApi.getState().features.userRegistration;
    const validationError = verifySetupAccountFields(state);

    if (validationError) {
      return thunkApi.rejectWithValue(validationError);
    }

    const response = await apiManager.registrationApi.getStatusRegistrazione(
      state.userCode
    );
    return response.data;
  } catch (error) {
    return thunkApi.rejectWithValue(getSetupAccountError(error));
  }
});

export const setupAccount = createAsyncThunk<
  Persona,
  void,
  AsyncThunkConfig<SetupAccountError>
>('setup_account', async (_, thunkApi) => {
  try {
    const state = thunkApi.getState().features.userRegistration;
    const validationError = verifySetupAccountFields(state);

    if (validationError) {
      return thunkApi.rejectWithValue(validationError);
    }

    const response = await apiManager.registrationApi.creaUtente(
      parseCreateUserRequest(state)
    );
    return response.data;
  } catch (error) {
    return thunkApi.rejectWithValue(getSetupAccountError(error));
  }
});

export const privacyAgreement = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig<PrivacyAgreementError>
>('privacy_agreement', async (_, thunkApi) => {
  try {
    const state = thunkApi.getState().features.userRegistration;

    await apiManager.registrationApi.aggiornaConsensoPrivacy(state.userCodeBE, {
      consensoTrattamentoDati: state.privacy,
    });
    return null;
  } catch (error) {
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const insertEmail = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig<InsertEmailError>
>('insert_email', async (_, thunkApi) => {
  try {
    const state = thunkApi.getState().features.userRegistration;

    const validationError = verifyInsertEmailFields(state);
    if (validationError) {
      return thunkApi.rejectWithValue(validationError);
    }

    await apiManager.registrationApi.censimentoEmail(state.userCodeBE, {
      email: state.email,
    });
    return null;
  } catch (error) {
    console.error(error);
    const apiError = parseApiResponseError<AggiornaEmailError>(error);
    const errorCodes = apiError.payload?.codiciErrore;

    if (apiError.responseErrorCode === ApiErrorCodes.BAD_REQUEST) {
      if (errorCodes?.length > 0) {
        switch (errorCodes[0]) {
          case AggiornaEmailErrorCodiciErroreEnum.EmailNonUtilizzabile:
            return thunkApi.rejectWithValue('EMAIL_NON_UTILIZZABILE');
        }
      }
    }
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const verificationEmail = createAsyncThunk<
  VerificaEmailResponse,
  void,
  AsyncThunkConfig<VerificationEmailError>
>('verification_email', async (_, thunkApi) => {
  try {
    const state = thunkApi.getState().features.userRegistration;

    const response = await apiManager.registrationApi.verificaEmail(
      state.userCodeBE,
      {
        email: state.email,
        codiceDiVerifica: state.verificationEmailCode,
      }
    );
    return response.data;
  } catch (error) {
    console.error(error);
    const apiError = parseApiResponseError<VerificaEmailError>(error);
    const errorCodes = apiError.payload?.codiciErrore;
    const attempts = apiError.payload?.tentativiRimastiOTP;

    if (apiError.responseErrorCode === ApiErrorCodes.BAD_REQUEST) {
      if (errorCodes?.length > 0) {
        switch (errorCodes[0]) {
          case VerificaEmailErrorCodiciErroreEnum.CodiceDiVerificaSbagliato:
            if (attempts === 0) {
              return thunkApi.rejectWithValue({ type: 'TROPPI_TENTATIVI' });
            }
            return thunkApi.rejectWithValue({
              type: 'CODICE_DI_VERIFICA_SBAGLIATO',
              attempts,
            });
          case VerificaEmailErrorCodiciErroreEnum.TroppiTentativi:
            return thunkApi.rejectWithValue({ type: 'TROPPI_TENTATIVI' });
          case VerificaEmailErrorCodiciErroreEnum.AccordoQuadroNonAttivo:
            return thunkApi.rejectWithValue({
              type: 'ACCORDO_QUADRO_NON_ATTIVO',
            });
        }
      }
    }
    return thunkApi.rejectWithValue({ type: 'GENERIC_ERROR' });
  }
});

export const insertPhoneNumber = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig<InsertPhoneNumberError>
>('insert_phone_number', async (_, thunkApi) => {
  try {
    const state = thunkApi.getState().features.userRegistration;

    const validationError = verifyInsertPhoneNumberFields(state);
    if (validationError) {
      return thunkApi.rejectWithValue(validationError);
    }

    await apiManager.registrationApi.censimentoCellulare(state.userCodeBE, {
      cellulare: parsePhoneNumberForServer(state.phoneNumber),
    });
    return null;
  } catch (error) {
    console.error(error);
    const apiError = parseApiResponseError<AggiornaCellulareError>(error);
    const errorCodes = apiError.payload?.codiciErrore;

    if (apiError.responseErrorCode === ApiErrorCodes.BAD_REQUEST) {
      if (errorCodes?.length > 0) {
        switch (errorCodes[0]) {
          case AggiornaCellulareErrorCodiciErroreEnum.CellulareNonUtilizzabile:
            return thunkApi.rejectWithValue('CELLULARE_NON_UTILIZZABILE');
        }
      }
    }
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const insertSecondaryPhoneNumber = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig<InsertPhoneNumberError>
>('insert_secondary_phone_number', async (_, thunkApi) => {
  try {
    const state = thunkApi.getState().features.userRegistration;

    const validationError = verifyInsertSecondaryPhoneNumberFields(state);
    if (validationError) {
      return thunkApi.rejectWithValue(validationError);
    }

    await apiManager.registrationApi.censimentoCellulareSecondario(
      state.userCodeBE,
      {
        cellulare: parsePhoneNumberForServer(state.secondaryPhoneNumber),
      }
    );
    return null;
  } catch (error) {
    console.error(error);
    const apiError = parseApiResponseError<AggiornaCellulareError>(error);
    const errorCodes = apiError.payload?.codiciErrore;

    if (apiError.responseErrorCode === ApiErrorCodes.BAD_REQUEST) {
      if (errorCodes?.length > 0) {
        switch (errorCodes[0]) {
          case AggiornaCellulareErrorCodiciErroreEnum.CellulareNonUtilizzabile:
            return thunkApi.rejectWithValue('CELLULARE_NON_UTILIZZABILE');
        }
      }
    }
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const verificationPhoneNumber = createAsyncThunk<
  VerificaCellulareResponse,
  void,
  AsyncThunkConfig<VerificationPhoneNumberError>
>('verification_phone_number', async (_, thunkApi) => {
  try {
    const state = thunkApi.getState().features.userRegistration;

    const response = await apiManager.registrationApi.verificaCellulare(
      state.userCodeBE,
      {
        cellulare: parsePhoneNumberForServer(state.phoneNumber),
        codiceDiVerifica: state.verificationPhoneNumberCode,
      }
    );
    return response.data;
  } catch (error) {
    console.error(error);
    const apiError = parseApiResponseError<VerificaCellulareError>(error);
    const errorCodes = apiError.payload?.codiciErrore;
    const attempts = apiError.payload?.tentativiRimastiOTP;

    if (apiError.responseErrorCode === ApiErrorCodes.BAD_REQUEST) {
      if (errorCodes?.length > 0) {
        switch (errorCodes[0]) {
          case VerificaCellulareErrorCodiciErroreEnum.CodiceDiVerificaSbagliato:
            if (attempts === 0) {
              return thunkApi.rejectWithValue({ type: 'TROPPI_TENTATIVI' });
            }
            return thunkApi.rejectWithValue({
              type: 'CODICE_DI_VERIFICA_SBAGLIATO',
              attempts,
            });
          case VerificaCellulareErrorCodiciErroreEnum.TroppiTentativi:
            return thunkApi.rejectWithValue({ type: 'TROPPI_TENTATIVI' });
          case VerificaCellulareErrorCodiciErroreEnum.AccordoQuadroNonAttivo:
            return thunkApi.rejectWithValue({
              type: 'ACCORDO_QUADRO_NON_ATTIVO',
            });
        }
      }
    }
    return thunkApi.rejectWithValue({ type: 'GENERIC_ERROR' });
  }
});

export const insertFiscalCodeLegalPerson = createAsyncThunk<
  CodiceClienteRappresentante,
  void,
  AsyncThunkConfig<InsertFiscalCodeLegalPersonError>
>('insert_fiscal_code_legal_person', async (_, thunkApi) => {
  try {
    const state = thunkApi.getState().features.userRegistration;

    const validationError = verifyInsertFiscalCodeLegalPersonFields(state);
    if (validationError) {
      return thunkApi.rejectWithValue(validationError);
    }

    const response =
      await apiManager.registrationApi.censimentoCodiceFiscaleRappresentante(
        state.userCodeBE,
        {
          codiceFiscaleRappresentante: state.fiscalCodeLegalPerson,
        }
      );
    return response.data;
  } catch (error) {
    console.error(error);
    const apiError = parseApiResponseError<UtentiError>(error);
    const errorCodes = apiError.payload?.codiciErrore;

    if (apiError.responseErrorCode === ApiErrorCodes.BAD_REQUEST) {
      if (errorCodes?.length > 0) {
        switch (errorCodes[0]) {
          case UtentiErrorCodiciErroreEnum.AttivazioneImpossibile:
            return thunkApi.rejectWithValue('ATTIVAZIONE_IMPOSSIBILE');
          case UtentiErrorCodiciErroreEnum.UtenteNonValido:
            return thunkApi.rejectWithValue('UTENTE_NON_VALIDO');
          case UtentiErrorCodiciErroreEnum.UtenteInesistente:
            return thunkApi.rejectWithValue('UTENTE_INESISTENTE');
          case UtentiErrorCodiciErroreEnum.CfNonAssociato:
            return thunkApi.rejectWithValue('CF_NON_ASSOCIATO');
          case UtentiErrorCodiciErroreEnum.CfNonAssociatoFiduciaria:
            return thunkApi.rejectWithValue('CF_NON_ASSOCIATO_FIDUCIARIA');
          case UtentiErrorCodiciErroreEnum.PIvaNonAssociata:
            return thunkApi.rejectWithValue('P_IVA_NON_ASSOCIATA');
          case UtentiErrorCodiciErroreEnum.DocumentoNonAmmesso:
            return thunkApi.rejectWithValue('DOCUMENTO_NON_AMMESSO');
          case UtentiErrorCodiciErroreEnum.DocumentoScaduto:
            return thunkApi.rejectWithValue('DOCUMENTO_SCADUTO');
          case UtentiErrorCodiciErroreEnum.VincoliPresenti:
            return thunkApi.rejectWithValue('VINCOLI_PRESENTI');
          case UtentiErrorCodiciErroreEnum.UtenteGiaRegistrato:
            return thunkApi.rejectWithValue('UTENTE_GIA_REGISTRATO');
          case UtentiErrorCodiciErroreEnum.UtenzaNonMigrata:
            return thunkApi.rejectWithValue('UTENZA_NON_MIGRATA');
          case UtentiErrorCodiciErroreEnum.CfNoFiduciaria:
            return thunkApi.rejectWithValue('CF_NO_FIDUCIARIE');
        }
      }
    }
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const retrieveSignatureDocument = createAsyncThunk<
  SignatureDocument,
  void,
  AsyncThunkConfig<SignatureDocumentError>
>('retrieve_signature_document', async (_, thunkApi) => {
  try {
    return await signatureAreaRepository.downloadSignatureDocumentForRegistration(
      thunkApi
    );
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const prepareRegistration = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig<PrepareRegistrationError>
>('prepare_registration', async (_, thunkApi) => {
  try {
    const state = thunkApi.getState().features.userRegistration;

    await apiManager.registrationApi.procediAllaRegistrazione(state.userCodeBE);

    return null;
  } catch (error) {
    console.error(error);

    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});
